import { convertPattern, ConvertPatternError, convertPatternErrorToString, IpaOrFeature } from "../pattern/convertPattern";
import { Pattern } from "../pattern/pattern";
import { PhonemeInfo } from "../pattern/phonemeInfo";
import { SyllableContext } from "../pattern/checkSyllableContext";
import { IpaType } from "../phonemes/ipa";
import { PhonemeItem } from "../phonemes/segmentPhonemes";
import { intoSegments, SegmentStrategy } from "./intoSegments";
import { FeatureAttribute, Rule } from "./rule";
import { Feature } from "../phonemes/features";
import { PostActionType } from "../pattern/searchAndReplace";

export interface CompiledRule {
  from: IpaType[];
  to: IpaType[];
  context?: RuleContext;
  post?: PostActionType;
}
export function isCompiledRule(check: unknown): check is CompiledRule {
  const test = check as CompiledRule;
  return test.from !== undefined && test.to !== undefined;
}

/** what needs to hold for a rule to apply */
interface RuleContext {
  previous?: Pattern<PhonemeInfo>;
  next?: Pattern<PhonemeInfo>;
  syllable?: SyllableContext;
}

export interface RuleError {
  fromUnsupported?: PhonemeItem[];
  toUnsupported?: PhonemeItem[];
  contextUnsupported?: PhonemeItem[];
  patternErrors?: ConvertPatternError[];
}
export function isRuleError(check: unknown): check is RuleError {
  const test = check as RuleError;
  return test &&
    (test.fromUnsupported !== undefined || test.toUnsupported !== undefined
      || test.contextUnsupported !== undefined || test.patternErrors !== undefined);
}
export function isRuleErrors(check: unknown): check is RuleError[] {
  return Array.isArray(check) && check.length > 0 && isRuleError(check[0]);
}
export function ruleErrorToString(error: RuleError): string {
  let symbols = '';
  if (error.fromUnsupported) {
    symbols = symbols.concat(error.fromUnsupported.map(pi => pi.phoneme).join(', '));
  }
  if (error.toUnsupported) {
    symbols = symbols.concat(error.toUnsupported.map(pi => pi.phoneme).join(', '));
  }
  if (error.contextUnsupported) {
    symbols = symbols.concat(error.contextUnsupported.map(pi => pi.phoneme).join(', '));
  }

  let s = '';
  if (symbols !== '') {
    s = s.concat(`unsupported symbol: ${symbols}`);
  }

  if (error.patternErrors) {
    const pe = error.patternErrors.map(pe => convertPatternErrorToString(pe)).join(', ');
    s = s.concat(pe);
  }
  return s;
}

/** validate rule & turn it into a form we can run */
export function compileRule(rule: Rule, input?: SegmentStrategy, output?: SegmentStrategy): CompiledRule | RuleError {
  input = input ?? SegmentStrategy.Ipa;
  const [fromHasUnknowns, fromPhonemes] = intoSegments(rule.from, input);
  if (fromHasUnknowns) {
    return { fromUnsupported: fromPhonemes };
  }
  const froms = fromPhonemes.map(p => p.phoneme);

  output = output === undefined && input !== undefined ? input : output ?? SegmentStrategy.Ipa;
  const [toHasUnknowns, toPhonemes] = intoSegments(rule.to, output);
  if (toHasUnknowns) {
    return { toUnsupported: toPhonemes };
  }
  const tos = toPhonemes.map(p => p.phoneme);

  const strategy = getStrategy(input, output);
  const context = makeRuleContext(rule, strategy);
  if (isRuleError(context)) {
    return context;
  }
  if (rule.post) {
    return context
      ? { from: froms, to: tos, context, post: rule.post }
      : { from: froms, to: tos, post: rule.post };
  } else {
    return context
      ? { from: froms, to: tos, context }
      : { from: froms, to: tos };
  }
}

/** try to figure out how we should segment the context, which may be ambiguous */
function getStrategy(input: SegmentStrategy, output: SegmentStrategy): SegmentStrategy | 'maybe-ipa' {
  if (input === output) {
    return input;
  } else if ((input === SegmentStrategy.Single && output.includes('ipa'))
    || (input.includes('ipa') && output === SegmentStrategy.Single)) {
    return 'maybe-ipa';   // 1st try segmenting IPA, fallback to raw
  } else if (input === SegmentStrategy.Single || output === SegmentStrategy.Single) {
    return SegmentStrategy.Single;  // lowest common denominator
  } else if ((input.includes('ipa') && output === SegmentStrategy.SynthSymbols)
    || (output.includes('ipa') && input === SegmentStrategy.SynthSymbols)) {
    return SegmentStrategy.Single;  // lowest common denominator
  } else if (input.includes('ipa') && output.includes('ipa')) {
    return SegmentStrategy.IpaPlus;
  }
  return SegmentStrategy.Single;
}

/** validate & process rule context */
function makeRuleContext(raw: Rule, strategy: SegmentStrategy | 'maybe-ipa'): RuleContext | RuleError | undefined {
  const previous = raw.previous ? makeFeatureContext(raw.previous, strategy) : undefined;
  if (isRuleError(previous)) {
    return previous;
  }
  const next = raw.next ? makeFeatureContext(raw.next, strategy) : undefined;
  if (isRuleError(next)) {
    return next;
  }

  if (raw.syllable) {
    if (previous !== undefined || next !== undefined) {
      return { previous, next, syllable: raw.syllable };
    } else {
      return { syllable: raw.syllable };
    }
  } else if (previous !== undefined || next !== undefined) {
    return { previous, next };
  }
  return undefined;
}

/** (string | FeatureAttribute[]) -> { ipa: IpaType[] } | { feature: Feature[] } | { special: 'word' } */
function makeFeatureContext(
  info: string | FeatureAttribute[] | Pattern<IpaOrFeature>, strategy: SegmentStrategy | 'maybe-ipa'
): Pattern<PhonemeInfo> | RuleError {
  if (Array.isArray(info)) {
    if (info[0] === '#word') {
      return { special: 'word' };
    }
    return { feature: info as Feature[] };
  }

  if (typeof info === 'string') {
    if (strategy === 'maybe-ipa') {
      // first try IPA
      const [hasUnknowns, phonemes] = intoSegments(info, SegmentStrategy.Ipa);
      if (!hasUnknowns) {
        return { ipa: phonemes.map(p => p.phoneme) }; // succeeded w/IPA
      }
      // that didn't work, so fallback to raw
      strategy = SegmentStrategy.Single;
    }

    const [hasUnknowns, phonemes] = intoSegments(info, strategy);
    if (hasUnknowns) {
      return { contextUnsupported: phonemes };
    }
    const ipa = phonemes.map(p => p.phoneme);
    return { ipa };
  }

  if (strategy === 'maybe-ipa') {
    // first try IPA
    const result = convertPattern(info, undefined, SegmentStrategy.Ipa);
    if (result.errors.length === 0) {
      return result.pattern;  // succeeded w/IPA
    }
    // that didn't work, so fallback to raw
    strategy = SegmentStrategy.Single;
  }

  const result = convertPattern(info, undefined, strategy);
  if (result.errors.length > 0) {
    return { patternErrors: result.errors };
  }
  return result.pattern;
}
